import axios from 'axios';

/**
 * Expired-session guard.
 *
 * There is no refresh token: once the access token expires every private call answers 401. Opening
 * the admin after a while (typically on a phone) used to fire the whole boot + page burst — getMe,
 * restaurants, entities, notifications, dashboard sections, sockets — with the dead token, each
 * failing on its own and some retrying, which hammered the API before the app finally fell back to
 * login. Now the first sign of a dead session (an expired JWT before sending, or the first 401)
 * marks the session expired: every later private call is cancelled in the browser (never reaches
 * the server) and the app logs out exactly once.
 *
 * The flag is tied to the token that expired, so a fresh login (new token) clears it by itself.
 */

const EXPIRY_SKEW_MS = 30 * 1000;

let expiredToken = null;
let onExpired = null;

const readToken = () => {
  try {
    return localStorage.getItem('access_token');
  } catch (e) {
    return null;
  }
};

/** JWT `exp` check. Tokens that aren't readable JWTs are never treated as expired here (the 401 path still applies). */
export const isTokenExpired = (token) => {
  if (!token || typeof token !== 'string') return false;
  const parts = token.split('.');
  if (parts.length !== 3) return false;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    if (typeof payload?.exp !== 'number') return false;
    return payload.exp * 1000 <= Date.now() + EXPIRY_SKEW_MS;
  } catch (e) {
    return false;
  }
};

/** Registers what to do once when the session dies (the store wires a LOGOUT_USER dispatch). */
export const setSessionExpiredHandler = (handler) => {
  onExpired = handler;
};

export const isSessionExpired = () => {
  const token = readToken();
  if (!token) return false;
  return token === expiredToken || isTokenExpired(token);
};

export const markSessionExpired = (token = readToken()) => {
  if (!token || token === expiredToken) return;
  expiredToken = token;
  if (onExpired) {
    try {
      onExpired();
    } catch (e) {
      // never let the logout handler break the request pipeline
    }
  }
};

const cancelled = (config) =>
  new axios.CanceledError('Session expired', axios.AxiosError.ERR_CANCELED, config);

/** Request side: don't send anything with a token we already know is dead. */
export const blockWhenSessionExpired = (config) => {
  const token = readToken();
  if (token && (token === expiredToken || isTokenExpired(token))) {
    markSessionExpired(token);
    throw cancelled(config);
  }
  return config;
};

/** Response side: the first 401 ends the session for every other caller too. */
export const expireSessionOn401 = (error) => {
  if (error?.response?.status === 401) {
    const sentAuth = error?.config?.headers?.Authorization || '';
    const sentToken = String(sentAuth).replace(/^Bearer\s+/i, '');
    // Only if the 401 belongs to the token still in storage (not a stale call from before a re-login).
    if (sentToken && sentToken === readToken()) markSessionExpired(sentToken);
  }
  return Promise.reject(error);
};
